import { useState, useEffect, useRef } from "react";
import { ArrowUpRight, Plus, Download } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";
import { useReveal } from "@/hooks/useReveal";
import { decodeEmail, openEmail } from "@/lib/email";
import { attachBlobVideo } from "@/lib/blobVideo";

const SOCIALS = [
  { label: "LinkedIn", href: BRAND.linkedin },
  { label: "Email", email: true },
  { label: "Résumé (PDF)", href: BRAND.resume },
  { label: "Call", href: `tel:${BRAND.phone}` },
];

export default function Contact() {
  const ref = useReveal();
  const email = decodeEmail();
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const targetTime = useRef(0);
  const smoothTime = useRef(0);
  const rafRef = useRef(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduced(mq.matches);
    set();
    mq.addEventListener("change", set);
    return () => mq.removeEventListener("change", set);
  }, []);

  // map the footer's travel through the viewport -> scrub progress
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      const v = videoRef.current;
      if (!el || !v) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh - rect.top) / rect.height));
      if (v.duration && isFinite(v.duration) && !reduced) targetTime.current = p * v.duration;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reduced]);

  // Load footer video into memory (blob URL) so scrubbing works without host range support.
  useEffect(() => {
    let url;
    let cancelled = false;
    attachBlobVideo(videoRef.current, [
      { src: "/footer.webm", type: "video/webm" },
      { src: "/footer.mp4", type: "video/mp4" },
    ]).then((u) => {
      url = u;
      if (cancelled && u) URL.revokeObjectURL(u);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  // smooth scrub loop
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduced) {
      v.muted = true;
      v.loop = true;
      v.play().catch(() => {});
      return;
    }
    let seeking = false;
    const onSeeked = () => {
      seeking = false;
    };
    v.addEventListener("seeked", onSeeked);
    const tick = () => {
      const vid = videoRef.current;
      if (vid && vid.readyState >= 1 && vid.duration && isFinite(vid.duration)) {
        smoothTime.current += (targetTime.current - smoothTime.current) * 0.12;
        if (!seeking && Math.abs(targetTime.current - smoothTime.current) > 0.01) {
          seeking = true;
          try {
            vid.currentTime = smoothTime.current;
          } catch (e) {
            seeking = false;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      v.removeEventListener("seeked", onSeeked);
    };
  }, [reduced]);

  return (
    <footer
      id="contact"
      ref={(node) => {
        sectionRef.current = node;
        if (ref) ref.current = node;
      }}
      data-testid="contact-section"
      className="relative pt-28 sm:pt-36 grain overflow-hidden"
    >
      {/* scroll-scrubbed video background */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        muted
        playsInline
        preload="auto"
        autoPlay={reduced}
        loop={reduced}
        data-testid="footer-video"
      >
      </video>
      {/* legibility overlays */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,6,6,0.92) 0%, rgba(8,6,6,0.62) 30%, rgba(8,6,6,0.55) 65%, rgba(8,6,6,0.92) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none mix-blend-multiply"
        style={{ background: "radial-gradient(70% 80% at 70% 70%, rgba(122,20,12,0.5), transparent 70%)" }}
      />
      <div className="glow-field" style={{ opacity: 0.6, mixBlendMode: "screen" }} />

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        {/* CTA */}
        <div className="reveal">
          <span className="label">Start here</span>
          <h2 className="display mt-6 text-[2.75rem] sm:text-[5rem] lg:text-[6rem] text-[var(--ink)] max-w-4xl leading-[0.95]">
            Let's create, <span className="serif-italic">together!</span>
          </h2>
          <p className="mt-7 max-w-xl text-[var(--ink-dim)] leading-relaxed">
            If you're looking for someone who can bring together design, digital strategy, communication and practical execution, let's connect. I work with businesses, agencies, churches and ministries on creative, communications, web, UX/UI and marketing.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => openEmail("Let's connect")}
              data-testid="contact-email-btn"
              className="btn-light flex items-center gap-2 rounded-full pl-6 pr-2 py-3 text-sm font-medium"
            >
              {email}
              <span className="grid place-items-center h-8 w-8 rounded-full bg-[#0a0707] text-[var(--ink)]">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </button>
            <a
              href={BRAND.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-linkedin-btn"
              className="chip rounded-full px-6 py-3 text-sm text-[var(--ink)] hover:border-[var(--line-strong)] transition-colors"
            >
              LinkedIn
            </a>
            <a
              href={BRAND.resume}
              download
              data-testid="contact-resume-btn"
              className="chip rounded-full px-5 py-3 text-sm text-[var(--ink)] hover:border-[var(--line-strong)] transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download résumé
            </a>
          </div>
        </div>

        {/* footer grid */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-[var(--line)] pt-12">
          <div className="col-span-2 md:col-span-1">
            <a href="#top" className="flex items-center gap-3">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--ink)] text-[#0a0707]">
                <Plus className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <span className="text-[15px] font-medium text-[var(--ink)]">{BRAND.name}</span>
            </a>
            <p className="mt-5 text-sm text-[var(--ink-dim)] max-w-[15rem] leading-relaxed">
              {BRAND.tagline} — {BRAND.based}.
            </p>
          </div>
          <div>
            <div className="label mb-4">Menu</div>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="nav-link text-sm">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label mb-4">Elsewhere</div>
            <ul className="space-y-2.5">
              {SOCIALS.map((s) =>
                s.email ? (
                  <li key={s.label}>
                    <button
                      type="button"
                      onClick={() => openEmail()}
                      data-testid="footer-email-link"
                      className="nav-link text-sm"
                    >
                      {s.label}
                    </button>
                  </li>
                ) : (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      target={s.href.startsWith("http") ? "_blank" : undefined}
                      rel={s.href.startsWith("http") ? "noreferrer" : undefined}
                      className="nav-link text-sm"
                    >
                      {s.label}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>
          <div>
            <div className="label mb-4">Say hi</div>
            <button
              type="button"
              onClick={() => openEmail()}
              data-testid="footer-email-say-hi"
              className="nav-link text-sm block text-left"
            >
              {email}
            </button>
            <a href={`tel:${BRAND.phone}`} className="nav-link text-sm block mt-2.5">
              {BRAND.phone}
            </a>
            <p className="label mt-6 !text-[0.6rem]">Studio since 2007</p>
          </div>
        </div>

        <div className="py-8 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[var(--line)] mt-12">
          <span className="label !text-[0.6rem]">© 2026 {BRAND.name}. All rights reserved.</span>
          <span className="label !text-[0.6rem]">{BRAND.tagline}</span>
        </div>
      </div>
    </footer>
  );
}
